import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { FieldsetDataHooks } from '../internal/Fieldset';
import { DATA_HOOKS } from './dataHooks';

export interface FileUploadDriver extends BaseUniDriver {
  getExplanationText(): Promise<string>;
  getSingleFileText(): Promise<string>;
  getLang(): Promise<string>;
  getErrorMessage(): Promise<string>;
  openPopover(): Promise<boolean>;
  isPopoverOpen(): Promise<boolean>;
  getFilesNames(): Promise<string[]>;
  getFilesErrors(): Promise<(string | undefined)[]>;
  isFileLoading(index: number): Promise<boolean>;
  clickPopoverDeleteOn(index: number): Promise<void>;
  clickDeleteOnSingleFile(): Promise<void>;
}

export const fileUploadDriverFactory = (base: UniDriver): FileUploadDriver => {
  const getHook = (hook: string) => `[data-hook="${hook}"]`;
  const isPopoverOpen = () =>
    base.$(getHook(DATA_HOOKS.popoverContent)).exists();
  const getFilesInPopover = async () => {
    const files = base.$$(getHook(DATA_HOOKS.popoverFile));

    const count = await files.count();
    const fileElements = [];
    for (let i = 0; i < count; i += 1) {
      fileElements.push(files.get(i));
    }
    return fileElements;
  };
  return {
    ...baseUniDriverFactory(base),
    getExplanationText: async () => {
      return base.$(getHook(DATA_HOOKS.explanation)).text();
    },
    getSingleFileText: async () => {
      return base.$(getHook(DATA_HOOKS.singleFile)).text();
    },
    getErrorMessage: async () => {
      return base.$(getHook(FieldsetDataHooks.Error)).text();
    },
    isPopoverOpen: async () => {
      return isPopoverOpen();
    },
    openPopover: async () => {
      await base.$(getHook(DATA_HOOKS.popoverButton)).click();
      return isPopoverOpen();
    },
    getFilesNames: async () => {
      const files = await getFilesInPopover();
      const fileNames = await Promise.all(
        files.map(async (file) => {
          return file.$(getHook(DATA_HOOKS.popoverFileName)).text();
        }),
      );
      return fileNames;
    },
    getFilesErrors: async () => {
      const files = await getFilesInPopover();
      const fileErrors = await Promise.all(
        files.map(async (file) => {
          const exists = await file
            .$(getHook(DATA_HOOKS.popoverFileError))
            .exists();
          if (exists) {
            return file.$(getHook(DATA_HOOKS.popoverFileError)).text();
          }
          return;
        }),
      );
      return fileErrors;
    },
    getLang: async () => base.attr('lang'),
    isFileLoading: async (index) => {
      const files = await getFilesInPopover();
      return files[index].$(getHook(DATA_HOOKS.spinner)).exists();
    },
    clickPopoverDeleteOn: async (index) => {
      const files = await getFilesInPopover();
      await files[index]?.$(`${getHook(DATA_HOOKS.delete)} button`).click();
    },
    clickDeleteOnSingleFile: async () => {
      await base.$(`${getHook(DATA_HOOKS.delete)} button`).click();
    },
  };
};
